/**
 * EditorToolbar — Dark pill-style floating toolbar
 * Matches the WYSIWYG design with clean white icons on dark background.
 */

import React, { useRef, useState, useMemo } from 'react'
import { Editor } from '@tiptap/react'
import { useAnnotationStore } from '../../store/annotations'
import { extractHeadings } from '../../lib/tiptap'

export type ExportFormat = 'md' | 'pdf'

interface EditorToolbarProps {
  editor: Editor
  compact?: boolean
  currentCloudId?: string | null
  onInsertViewSnapshot?: () => void
  onExport?: (format: ExportFormat) => void
}

// ─── Icon-Komponenten ─────────────────────────────────────────────────────────

const SZ = 15  // Icon-Größe

function IconBold() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
  )
}

function IconItalic() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <line x1="19" y1="4" x2="10" y2="4" />
      <line x1="14" y1="20" x2="5" y2="20" />
      <line x1="15" y1="4" x2="9" y2="20" />
    </svg>
  )
}

function IconStrike() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M17.3 12H6.7" strokeWidth="2.2" />
      <path d="M10 7.5c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5" />
      <path d="M8.2 17c.5 1.2 1.8 2 3.3 2 2.2 0 3.9-1.3 3.9-3" />
    </svg>
  )
}

function IconH({ level }: { level: 1 | 2 | 3 }) {
  const sub = ['₁', '₂', '₃'][level - 1]
  return (
    <span style={{ fontFamily: 'var(--font-sans, system-ui)', fontWeight: 700, fontSize: '0.85em', letterSpacing: '-0.03em', lineHeight: 1 }}>
      H{sub}
    </span>
  )
}

function IconOrderedList() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="10" y1="6" x2="21" y2="6" />
      <line x1="10" y1="12" x2="21" y2="12" />
      <line x1="10" y1="18" x2="21" y2="18" />
      <text x="4" y="7" fill="currentColor" stroke="none" fontSize="6" fontFamily="system-ui" fontWeight="600" textAnchor="middle">1</text>
      <text x="4" y="13" fill="currentColor" stroke="none" fontSize="6" fontFamily="system-ui" fontWeight="600" textAnchor="middle">2</text>
      <text x="4" y="19" fill="currentColor" stroke="none" fontSize="6" fontFamily="system-ui" fontWeight="600" textAnchor="middle">3</text>
    </svg>
  )
}

function IconBulletList() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="9" y1="6" x2="21" y2="6" />
      <line x1="9" y1="12" x2="21" y2="12" />
      <line x1="9" y1="18" x2="21" y2="18" />
      <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  )
}

function IconCode() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  )
}

function IconLink() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}

function IconImage() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  )
}

function IconAudit() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  )
}

function IconPaperless() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a1 1 0 0 0 1 1h3" />
      <path d="M10 13h4" />
      <path d="M10 17h4" />
      <path d="M10 9h1" />
    </svg>
  )
}

function IconAnnotation() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="10" r="1.5" fill="currentColor" />
    </svg>
  )
}

function IconSnapshot() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 7l-7 5 7 5V7z"/>
      <rect x="1" y="5" width="15" height="14" rx="2"/>
      <circle cx="8.5" cy="12" r="2.5"/>
    </svg>
  )
}

function IconUndo() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 14 4 9 9 4" />
      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
    </svg>
  )
}

function IconRedo() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 14 20 9 15 4" />
      <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
    </svg>
  )
}

function IconExport() {
  return (
    <svg width={SZ} height={SZ} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

// ─── Basis-Button ─────────────────────────────────────────────────────────────

function Btn({
  active, disabled, title, onClick, children, labeled,
}: {
  active?: boolean; disabled?: boolean; title: string; onClick: () => void; children: React.ReactNode; labeled?: boolean
}) {
  return (
    <button
      className={`doc-panel-toolbar-btn${active ? ' is-active' : ''}${labeled ? ' doc-panel-toolbar-btn--labeled' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <div className="doc-panel-toolbar-divider" />
}

// ─── Komponente ───────────────────────────────────────────────────────────────

export function EditorToolbar({ editor, compact = false, currentCloudId, onInsertViewSnapshot, onExport }: EditorToolbarProps) {
  const [showLinkInput,        setShowLinkInput]        = useState(false)
  const [linkUrl,              setLinkUrl]              = useState('')
  const [showAnnotationPicker, setShowAnnotationPicker] = useState(false)
  const [showPaperlessPicker, setShowPaperlessPicker] = useState(false)
  const [showAuditPicker, setShowAuditPicker] = useState(false)
  const [paperlessDocs, setPaperlessDocs] = useState<any[]>([])
  const [paperlessLoading, setPaperlessLoading] = useState(false)
  const [annSearch,            setAnnSearch]            = useState('')
  const [auditSearch,          setAuditSearch]          = useState('')
  const [docSearch,            setDocSearch]             = useState('')

  const imageInputRef = useRef<HTMLInputElement>(null)
  const annotations   = useAnnotationStore(s => s.annotations)

  const cloudAnnotations = annotations.filter(
    a => !currentCloudId || a.cloudId === currentCloudId
  )

  const auditObjects: any[] = (window as any)._auditObjects || []
  const cloudAudits = auditObjects.filter(
    (a: any) => !currentCloudId || a.data?.cloudId === currentCloudId
  )


  function applyLink(overrideUrl?: string) {
    const val = overrideUrl ?? linkUrl
    if (!val) return
    const url = val.startsWith('#') ? val : val.startsWith('http') ? val : `https://${val}`
    editor.chain().focus().setLink({ href: url }).run()
    setLinkUrl(''); setShowLinkInput(false)
  }

  // Extract headings for anchor picker
  const headings = useMemo(() => {
    if (!showLinkInput) return []
    return extractHeadings(editor.state.doc)
  }, [showLinkInput, editor.state.doc])

  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = evt => {
      editor.chain().focus().setImage({ src: evt.target?.result as string, alt: file.name }).run()
    }
    reader.readAsDataURL(file); e.target.value = ''
  }

  function insertAnnotationLink(ann: { id: string; title: string }) {
    editor.chain().focus().insertAnnotationLink({ annotationId: ann.id, label: ann.title }).run()
    setShowAnnotationPicker(false)
  }

  // ── Paperless Doc Link ──────────────────────────────────────────────────
  async function openPaperlessPicker() {
    const willOpen = !showPaperlessPicker
    setShowPaperlessPicker(willOpen)
    if (willOpen) {
      // Bei jedem Oeffnen neu laden (Projekt-Tag koennte sich geaendert haben)
      setPaperlessLoading(true)
      try {
        const { fetchProjectDocuments } = await import('../../lib/paperless/api')
        const res = await fetchProjectDocuments({ page: 1, page_size: 30, ordering: '-created' })
        setPaperlessDocs(res.results)
      } catch { /* silent */ }
      setPaperlessLoading(false)
    }
  }

  function insertPaperlessDocLink(doc: any) {
    editor.chain().focus().insertPaperlessDocLink({
      docId: doc.id,
      title: doc.title,
      fileName: doc.original_file_name || '',
    }).run()
    setShowPaperlessPicker(false)
  }

  // ── Audit Link ──────────────────────────────────────────────────────────
  function insertAuditLink(audit: any) {
    editor.chain().focus().insertAuditLink({
      auditId: audit.id,
      label: audit.data.bezeichnung || 'Audit',
      potentiale: audit.data.potentiale || '',
    }).run()
    setShowAuditPicker(false)
  }

  if (!editor) return null

  return (
    <div className="doc-panel-toolbar" data-component="EditorToolbar">

      {/* ── Zeile 1: Bold · Italic | H1 · H2 · H3 | Link ── */}
      <div className="doc-panel-toolbar-row">
        <Btn active={editor.isActive('bold')}   title="Bold (Ctrl+B)"   onClick={() => editor.chain().focus().toggleBold().run()}><IconBold /></Btn>
        <Btn active={editor.isActive('italic')} title="Italic (Ctrl+I)" onClick={() => editor.chain().focus().toggleItalic().run()}><IconItalic /></Btn>

        <Divider />

        <Btn active={editor.isActive('heading', { level: 1 })} title="Überschrift 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><IconH level={1} /></Btn>
        <Btn active={editor.isActive('heading', { level: 2 })} title="Überschrift 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><IconH level={2} /></Btn>
        <Btn active={editor.isActive('heading', { level: 3 })} title="Überschrift 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><IconH level={3} /></Btn>

        <Divider />

        <Btn active={editor.isActive('orderedList')} title="Nummerierte Liste" onClick={() => editor.chain().focus().toggleOrderedList().run()}><IconOrderedList /></Btn>
        <Btn active={editor.isActive('bulletList')} title="Aufzählung" onClick={() => editor.chain().focus().toggleBulletList().run()}><IconBulletList /></Btn>

        <Divider />

        <Btn
          active={editor.isActive('link') || showLinkInput}
          title={editor.isActive('link') ? 'Link bearbeiten / entfernen' : 'Link einfügen'}
          onClick={() => {
            // Vorhandene URL vorladen wenn Cursor auf einem Link steht
            if (!showLinkInput && editor.isActive('link')) {
              setLinkUrl(editor.getAttributes('link').href ?? '')
            }
            setShowLinkInput(v => !v)
          }}
        ><IconLink /></Btn>

        <Btn title="Bild hochladen" onClick={() => imageInputRef.current?.click()}><IconImage /></Btn>
        <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />

        <Divider />

        <Btn title="Rückgängig (Ctrl+Z)" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><IconUndo /></Btn>
        <Btn title="Wiederholen (Ctrl+Y)" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><IconRedo /></Btn>
      </div>

      {/* ── Zeile 2: Dokument · Audit · View · Annotation ── */}
      <div className="doc-panel-toolbar-row">
        <Btn labeled active={showPaperlessPicker} title="Paperless Dokument einfügen" onClick={openPaperlessPicker}><IconPaperless /><span>Dokument</span></Btn>
        <Btn labeled active={showAuditPicker} title="Audit einfügen" onClick={() => setShowAuditPicker(v => !v)}><IconAudit /><span>Audit</span></Btn>
        {onInsertViewSnapshot && (
          <Btn labeled title="Aktuelle Ansicht einfügen" onClick={onInsertViewSnapshot}><IconSnapshot /><span>View</span></Btn>
        )}
        <Btn labeled active={showAnnotationPicker} title="Annotation verlinken" onClick={() => setShowAnnotationPicker(v => !v)}><IconAnnotation /><span>Annotation</span></Btn>
      </div>

      {/* ── Inline-Inputs ─────────────────────────────────────────────────── */}

      {showLinkInput && (
        <div className="doc-panel-link-input">
          <input
            className="doc-panel-link-input__field"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyLink(); if (e.key === 'Escape') setShowLinkInput(false) }}
            placeholder="https://… oder #anker"
            autoFocus
          />
          <div className="doc-panel-link-input__actions">
            <button className="doc-panel-link-input__btn doc-panel-link-input__btn--confirm" onClick={() => applyLink()}>
              Setzen
            </button>
            {editor.isActive('link') && (
              <button
                className="doc-panel-link-input__btn doc-panel-link-input__btn--remove"
                title="Link entfernen"
                onClick={() => { editor.chain().focus().unsetLink().run(); setShowLinkInput(false); setLinkUrl('') }}
              >
                Entfernen
              </button>
            )}
            <button className="doc-panel-link-input__btn doc-panel-link-input__btn--cancel" onClick={() => { setShowLinkInput(false); setLinkUrl('') }}>
              ✕
            </button>
          </div>
          {headings.length > 0 && (
            <div className="doc-panel-link-anchors">
              {headings.map((h, i) => (
                <button
                  key={i}
                  className="doc-panel-link-anchors__item"
                  style={{ paddingLeft: `${(h.level - 1) * 0.75 + 0.5}em` }}
                  onClick={() => applyLink(`#${h.slug}`)}
                >
                  <span className="doc-panel-link-anchors__level">H{h.level}</span>
                  {h.text}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Annotation Picker ──────────────────────────────────────────────── */}

      {showAnnotationPicker && (() => {
        const filtered = cloudAnnotations.filter(a =>
          !annSearch.trim() ||
          a.title.toLowerCase().includes(annSearch.toLowerCase()) ||
          (a.tooltip ?? '').toLowerCase().includes(annSearch.toLowerCase())
        )
        return (
          <div className="doc-panel-annotation-picker" data-section="annotation-picker">
            {/* Search */}
            <div className="doc-panel-annotation-picker__search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="doc-panel-annotation-picker__search-icon">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="doc-panel-annotation-picker__search-input"
                value={annSearch}
                onChange={e => setAnnSearch(e.target.value)}
                placeholder="Suchen…"
                autoFocus
              />
            </div>

            {/* Items */}
            <div className="doc-panel-annotation-picker__list">
              {filtered.length === 0 && (
                <div className="doc-panel-annotation-picker__empty">
                  Keine Annotationen gefunden
                </div>
              )}
              {filtered.map(ann => (
                <div
                  key={ann.id}
                  className="doc-panel-annotation-picker__item"
                  onClick={() => { insertAnnotationLink(ann); setAnnSearch('') }}
                >
                  <span className="doc-panel-annotation-picker__item-dot" />
                  <div className="doc-panel-annotation-picker__item-body">
                    <div className="doc-panel-annotation-picker__item-title">{ann.title}</div>
                    {ann.tooltip && (
                      <div className="doc-panel-annotation-picker__item-sub">{ann.tooltip}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="doc-panel-annotation-picker__footer">
              <button
                className="doc-panel-annotation-picker__footer-btn"
                onClick={() => { setShowAnnotationPicker(false); setAnnSearch('') }}
              >
                Schließen
              </button>
            </div>
          </div>
        )
      })()}

      {/* ── Audit Picker ──────────────────────────────────────────────── */}
      {showAuditPicker && (() => {
        const filtered = cloudAudits.filter((a: any) =>
          !auditSearch.trim() ||
          (a.data.bezeichnung || '').toLowerCase().includes(auditSearch.toLowerCase()) ||
          (a.data.funktion || '').toLowerCase().includes(auditSearch.toLowerCase()) ||
          (a.data.ebene || '').toLowerCase().includes(auditSearch.toLowerCase())
        )
        return (
          <div className="doc-panel-annotation-picker" data-section="audit-picker">
            <div className="doc-panel-annotation-picker__search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="doc-panel-annotation-picker__search-icon">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="doc-panel-annotation-picker__search-input"
                value={auditSearch}
                onChange={e => setAuditSearch(e.target.value)}
                placeholder="Audit suchen…"
                autoFocus
              />
            </div>
            <div className="doc-panel-annotation-picker__list">
              {filtered.length === 0 && (
                <div className="doc-panel-annotation-picker__empty">
                  Keine Audits gefunden
                </div>
              )}
              {filtered.map((audit: any) => (
                <div
                  key={audit.id}
                  className="doc-panel-annotation-picker__item"
                  onClick={() => { insertAuditLink(audit); setAuditSearch('') }}
                >
                  <span
                    className="doc-panel-annotation-picker__item-dot"
                    style={{
                      background: audit.data.potentiale === 'Ja' ? '#10b981'
                        : audit.data.potentiale === 'Nein' ? '#ef4444' : '#f59e0b',
                    }}
                  />
                  <div className="doc-panel-annotation-picker__item-body">
                    <div className="doc-panel-annotation-picker__item-title">
                      {audit.data.bezeichnung || 'Audit'}
                    </div>
                    {(audit.data.funktion || audit.data.ebene) && (
                      <div className="doc-panel-annotation-picker__item-sub">
                        {audit.data.funktion}{audit.data.ebene ? ` · ${audit.data.ebene}` : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="doc-panel-annotation-picker__footer">
              <button
                className="doc-panel-annotation-picker__footer-btn"
                onClick={() => { setShowAuditPicker(false); setAuditSearch('') }}
              >
                Schließen
              </button>
            </div>
          </div>
        )
      })()}

      {/* ── Paperless Doc Picker ──────────────────────────────────────── */}
      {showPaperlessPicker && (() => {
        const filtered = paperlessDocs.filter((d: any) =>
          !docSearch.trim() ||
          (d.title || '').toLowerCase().includes(docSearch.toLowerCase()) ||
          (d.original_file_name || '').toLowerCase().includes(docSearch.toLowerCase())
        )
        return (
          <div className="doc-panel-annotation-picker" data-section="paperless-picker">
            <div className="doc-panel-annotation-picker__search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="doc-panel-annotation-picker__search-icon">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="doc-panel-annotation-picker__search-input"
                value={docSearch}
                onChange={e => setDocSearch(e.target.value)}
                placeholder="Dokument suchen…"
                autoFocus
              />
            </div>
            <div className="doc-panel-annotation-picker__list">
              {paperlessLoading && (
                <div className="doc-panel-annotation-picker__empty">
                  Dokumente laden…
                </div>
              )}
              {!paperlessLoading && filtered.length === 0 && (
                <div className="doc-panel-annotation-picker__empty">
                  Keine Dokumente gefunden
                </div>
              )}
              {filtered.map((doc: any) => (
                <div
                  key={doc.id}
                  className="doc-panel-annotation-picker__item"
                  onClick={() => { insertPaperlessDocLink(doc); setDocSearch('') }}
                >
                  <span className="doc-panel-annotation-picker__item-dot" style={{ background: '#3b82f6' }} />
                  <div className="doc-panel-annotation-picker__item-body">
                    <div className="doc-panel-annotation-picker__item-title">{doc.title}</div>
                    <div className="doc-panel-annotation-picker__item-sub">
                      {doc.original_file_name || ''}
                      {doc.created ? ` · ${new Date(doc.created).toLocaleDateString('de-DE')}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="doc-panel-annotation-picker__footer">
              <button
                className="doc-panel-annotation-picker__footer-btn"
                onClick={() => { setShowPaperlessPicker(false); setDocSearch('') }}
              >
                Schließen
              </button>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
